<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useConvexQuery } from 'convex-vue';
import { useConvexQueryWithStaleDetection } from '../composables/useConvexQueryWithStaleDetection';
import { useConvexAuth } from '../composables/useConvexAuth';
import { api } from '../../convex/_generated/api';
import { useDateFormatter } from '../composables/useDateFormatter';

import CreateWorkshopModal from '../components/CreateWorkshopModal.vue';
import SettingsPanel from '../components/SettingsPanel.vue';
import InviteAcceptanceModal from '../components/InviteAcceptanceModal.vue';

const router = useRouter();
const route = useRoute();

const showCreateModal = ref(false);
const showSettings = ref(false);
const showInviteModal = ref(false);
const inviteToken = ref<string | null>(null);
const hasAttemptedInviteRecovery = ref(false);
const { getPendingInviteCode, clearPendingInviteCode } = useConvexAuth();

const { data: currentUserData, isPending: userPending } = useConvexQuery(
  api.auth.currentUser,
  () => ({})
);

const {
  data: workshopsData,
  isPending: workshopsPending,
  isReconnecting,
} = useConvexQueryWithStaleDetection(api.workshops.getMyWorkshops, () => ({}), {
  name: 'WorkshopsList',
  isEmpty: data => {
    if (!data) return true;
    return !data.owner?.length && !data.member?.length && !data.reader?.length;
  },
});

const isLoading = computed(() => userPending.value || workshopsPending.value);
const currentUser = computed(() => currentUserData.value);
const canCreate = computed(() => currentUser.value?.isWorkshopCreationAllowed);

const ownerWorkshops = computed(() => workshopsData.value?.owner || []);
const memberWorkshops = computed(() => workshopsData.value?.member || []);
const readerWorkshops = computed(() => workshopsData.value?.reader || []);

const hasWorkshops = computed(
  () =>
    ownerWorkshops.value.length > 0 ||
    memberWorkshops.value.length > 0 ||
    readerWorkshops.value.length > 0
);

// Generate random rotations for post-it cards
const getCardRotation = (index: number) => {
  const base = (index % 3) - 1;
  const random = (Math.random() - 0.5) * 2;
  return (base * 1.5 + random).toFixed(2);
};

// Post-it colors for different roles
const roleColors = {
  owner: 'bg-(--color-postit-pink)',
  member: 'bg-(--color-postit-blue)',
  reader: 'bg-(--color-postit-green)',
};

const navigateToWorkshop = (slug: string) => {
  router.push('/workshop/' + slug);
};

const { formatDate } = useDateFormatter();

// Check for invite token in URL on mount and when route changes
const checkForInviteToken = () => {
  const queryToken = route.query.invite as string | undefined;
  const paramToken = typeof route.params.code === 'string' ? route.params.code : undefined;
  const token = queryToken || paramToken;
  if (token) {
    inviteToken.value = token;
    showInviteModal.value = true;
    // Keep token in URL until accepted (prevents loss on refresh)
  }
};

onMounted(checkForInviteToken);

// Also watch for route changes (for already logged in users navigating to the page)
watch(() => route.query.invite, checkForInviteToken, { immediate: true });
watch(() => route.params.code, checkForInviteToken, { immediate: true });

watch(
  [isLoading, hasWorkshops],
  async ([loading, hasAnyWorkshops]) => {
    if (loading || hasAnyWorkshops || hasAttemptedInviteRecovery.value) {
      return;
    }

    hasAttemptedInviteRecovery.value = true;

    const pendingInviteCode = getPendingInviteCode();
    if (!pendingInviteCode) {
      return;
    }

    clearPendingInviteCode();
    await router.push(`/invite/${encodeURIComponent(pendingInviteCode)}`);
  },
  { immediate: true }
);

const handleInviteAccepted = (_workshopId: string, slug: string) => {
  clearPendingInviteCode();

  // Remove token from URL now that it's accepted
  const newQuery = { ...route.query };
  delete newQuery.invite;
  router.replace({ query: newQuery });

  // Navigate directly to the workshop
  if (slug) {
    router.push(`/workshop/${slug}`);
  } else {
    // Fallback: refresh the page to show the new workshop in the list
    window.location.reload();
  }
};
</script>
<template>
  <div class="cork-board min-h-screen">
    <!-- Wooden clipboard style header -->
    <header class="wood-texture border-b border-black/20 px-6 py-4 shadow-lg relative">
      <div
        class="absolute inset-0 opacity-40 pointer-events-none"
        style="
          background-image: url('/wood.jpg');
          background-size: cover;
          background-position: center;
        "
      ></div>

      <div class="max-w-7xl mx-auto flex items-center justify-between relative z-10">
        <div class="flex items-center gap-3">
          <svg
            class="hidden sm:block w-8 h-8 mr-1"
            viewBox="0 0 32 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <!-- Pink sticky note (back) -->
            <rect
              x="14"
              y="6"
              width="14"
              height="14"
              rx="1"
              fill="#FFB3D9"
              transform="rotate(8 21 13)"
            />
            <!-- Blue sticky note (middle) -->
            <rect
              x="8"
              y="8"
              width="14"
              height="14"
              rx="1"
              fill="#B3D9FF"
              transform="rotate(-5 15 15)"
            />
            <!-- Yellow sticky note (front) -->
            <rect
              x="4"
              y="12"
              width="14"
              height="14"
              rx="1"
              fill="#FFE680"
              transform="rotate(3 11 19)"
            />
          </svg>
          <h1 class="text-2xl font-bold text-white drop-shadow-md">
            {{ $t('workshop.myWorkshops') }}
          </h1>
        </div>

        <div class="flex items-center gap-3">
          <span v-if="currentUser" class="text-white/90 hidden sm:inline font-medium drop-shadow">{{
            currentUser.name || 'User'
          }}</span>

          <button
            v-if="currentUser"
            @click="showSettings = true"
            class="p-2.5 text-white/90 hover:text-white hover:bg-white/10 rounded-lg transition-colors shadow-sm cursor-pointer"
            title="Settings"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
              />
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          </button>

          <button
            v-if="canCreate"
            @click="showCreateModal = true"
            class="push-button px-5 py-2.5 rounded-lg text-sm font-semibold"
          >
            {{ $t('workshop.create') }}
          </button>
        </div>
      </div>
    </header>

    <main class="max-w-7xl mx-auto px-6 py-8">
      <div v-if="isLoading" class="text-center py-12">
        <div class="paper-card inline-block p-6 rounded-sm">
          <div class="paper-texture rounded-sm"></div>
          <div class="relative z-10">
            <div
              class="inline-block w-8 h-8 border-3 border-gray-300 border-t-gray-600 rounded-full animate-spin mb-3"
            ></div>
            <p class="text-gray-600">{{ $t('common.loading') }}</p>
          </div>
        </div>
      </div>

      <div v-else-if="!hasWorkshops" class="text-center py-12">
        <div
          v-if="isReconnecting"
          class="paper-card cork-pin inline-block p-8 rounded-sm max-w-md"
          :style="{ '--card-rotation': '1deg' }"
        >
          <div class="paper-texture rounded-sm"></div>
          <div class="relative z-10">
            <div
              class="inline-block w-8 h-8 border-3 border-gray-300 border-t-gray-600 rounded-full animate-spin mb-3"
            ></div>
            <p class="text-gray-700 text-lg">{{ $t('workshop.reconnecting') }}</p>
          </div>
        </div>
        <div
          v-else
          class="paper-card cork-pin inline-block p-8 rounded-sm max-w-md"
          :style="{ '--card-rotation': '1deg' }"
        >
          <div class="paper-texture rounded-sm"></div>
          <div class="relative z-10">
            <p class="text-gray-700 text-lg mb-4">{{ $t('workshop.noWorkshops') }}</p>
            <p v-if="canCreate" class="text-gray-500">{{ $t('workshop.createFirst') }}</p>
            <p v-else class="text-gray-500">{{ $t('workshop.waitForInvite') }}</p>
          </div>
        </div>
      </div>

      <div v-else class="space-y-10">
        <div v-if="ownerWorkshops.length > 0">
          <div class="flex items-center gap-3 mb-5">
            <div class="role-badge role-badge-owner">{{ $t('roles.owner') }}</div>
            <div class="h-px flex-1 bg-black/10"></div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            <div
              v-for="(workshop, index) in ownerWorkshops"
              :key="workshop.id"
              @click="navigateToWorkshop(workshop.slug)"
              class="paper-card cork-pin p-5 rounded-sm cursor-pointer transition-all duration-200 hover:scale-[1.02] hover:shadow-xl"
              :class="roleColors.owner"
              :style="{ '--card-rotation': getCardRotation(index) + 'deg' }"
            >
              <div class="paper-texture rounded-sm"></div>
              <div class="relative z-10">
                <div class="flex items-start justify-between mb-3">
                  <h3 class="text-gray-800 font-bold text-lg truncate pr-2">{{ workshop.name }}</h3>
                </div>
                <p class="text-gray-600 text-sm mb-3 font-medium">/{{ workshop.slug }}</p>
                <div class="flex items-center gap-3 text-gray-600 text-xs">
                  <span>{{ workshop.totalIdeas }} {{ $t('workshop.ideas') }}</span>
                  <span>{{ workshop.totalVotes }} {{ $t('workshop.votes') }}</span>
                  <span class="ml-auto text-gray-500">{{
                    formatDate(workshop.lastActivityAt)
                  }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="memberWorkshops.length > 0">
          <div class="flex items-center gap-3 mb-5">
            <div class="role-badge role-badge-member">{{ $t('roles.member') }}</div>
            <div class="h-px flex-1 bg-black/10"></div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            <div
              v-for="(workshop, index) in memberWorkshops"
              :key="workshop.id"
              @click="navigateToWorkshop(workshop.slug)"
              class="paper-card cork-pin p-5 rounded-sm cursor-pointer transition-all duration-200 hover:scale-[1.02] hover:shadow-xl"
              :class="roleColors.member"
              :style="{ '--card-rotation': getCardRotation(index) + 'deg' }"
            >
              <div class="paper-texture rounded-sm"></div>
              <div class="relative z-10">
                <div class="flex items-start justify-between mb-3">
                  <h3 class="text-gray-800 font-bold text-lg truncate pr-2">{{ workshop.name }}</h3>
                </div>
                <p class="text-gray-600 text-sm mb-3 font-medium">/{{ workshop.slug }}</p>
                <div class="flex items-center gap-3 text-gray-600 text-xs">
                  <span>{{ workshop.totalIdeas }} ideas</span>
                  <span>{{ workshop.totalVotes }} votes</span>
                  <span class="ml-auto text-gray-500">{{
                    formatDate(workshop.lastActivityAt)
                  }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="readerWorkshops.length > 0">
          <div class="flex items-center gap-3 mb-5">
            <div class="role-badge role-badge-reader">{{ $t('roles.reader') }}</div>
            <div class="h-px flex-1 bg-black/10"></div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            <div
              v-for="(workshop, index) in readerWorkshops"
              :key="workshop.id"
              @click="navigateToWorkshop(workshop.slug)"
              class="paper-card cork-pin p-5 rounded-sm cursor-pointer transition-all duration-200 hover:scale-[1.02] hover:shadow-xl"
              :class="roleColors.reader"
              :style="{ '--card-rotation': getCardRotation(index) + 'deg' }"
            >
              <div class="paper-texture rounded-sm"></div>
              <div class="relative z-10">
                <div class="flex items-start justify-between mb-3">
                  <h3 class="text-gray-800 font-bold text-lg truncate pr-2">{{ workshop.name }}</h3>
                </div>
                <p class="text-gray-600 text-sm mb-3 font-medium">/{{ workshop.slug }}</p>
                <div class="flex items-center gap-3 text-gray-600 text-xs">
                  <span>{{ workshop.totalIdeas }} ideas</span>
                  <span>{{ workshop.totalVotes }} votes</span>
                  <span class="ml-auto text-gray-500">{{
                    formatDate(workshop.lastActivityAt)
                  }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>

    <CreateWorkshopModal v-model="showCreateModal" @created="showCreateModal = false" />
    <SettingsPanel v-model="showSettings" />
    <InviteAcceptanceModal
      v-model="showInviteModal"
      :token="inviteToken"
      @accepted="handleInviteAccepted"
    />
  </div>
</template>
